import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import JobDetailsForm from './components/JobDetailsForm';
import EligibilityForm from './components/EligibilityForm';
import InternshipDetailsForm from './components/InternshipDetailsForm';
import ApplicationManagementForm from './components/ApplicationManagementForm';
import PreviewPanel from './components/PreviewPanel';
import JobTemplateLibrary from './components/JobTemplateLibrary';

const JobInternshipPosting = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [jobData, setJobData] = useState({
    title: '',
    company: '',
    companyDescription: '',
    responsibilities: '',
    qualifications: '',
    type: 'Full Time',
    location: '',
    workArrangement: 'onsite',
    salaryMin: '',
    salaryMax: '',
    educationLevel: [],
    requiredSkills: [],
    experienceMin: '',
    experienceMax: '',
    preferredLocations: [],
    isInternship: false,
    internshipDuration: '',
    stipendAmount: '',
    hasMentorship: false,
    mentorshipDetails: '',
    applicationDeadline: '',
    candidateLimit: '',
    screeningQuestions: []
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, label: 'Job Details', icon: 'Briefcase' },
    { id: 2, label: 'Eligibility', icon: 'GraduationCap' },
    { id: 3, label: 'Internship Details', icon: 'Clock' },
    { id: 4, label: 'Application', icon: 'Users' }
  ];

  const handleDataChange = (field, value) => {
    setJobData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!jobData?.title) newErrors.title = 'Job title is required';
      if (!jobData?.company) newErrors.company = 'Company name is required';
      if (!jobData?.companyDescription) newErrors.companyDescription = 'Company description is required';
      if (!jobData?.responsibilities) newErrors.responsibilities = 'Responsibilities are required';
      if (!jobData?.location) newErrors.location = 'Location is required';
    }
    
    if (step === 2) {
      if (jobData?.educationLevel?.length === 0) newErrors.educationLevel = 'At least one education level is required';
      if (jobData?.requiredSkills?.length === 0) newErrors.requiredSkills = 'At least one skill is required';
    }
    
    if (step === 3 && jobData?.isInternship) {
      if (!jobData?.internshipDuration) newErrors.internshipDuration = 'Duration is required for internships';
      if (!jobData?.stipendAmount) newErrors.stipendAmount = 'Stipend amount is required';
    }
    
    if (step === 4) {
      if (!jobData?.applicationDeadline) newErrors.applicationDeadline = 'Application deadline is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSaveDraft = () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert('Job posting saved as draft successfully!');
    }, 1500);
  };

  const handlePublish = () => {
    if (validateStep(1) && validateStep(2) && validateStep(4)) {
      if (window.confirm('Are you sure you want to publish this job posting? It will be visible to all students.')) {
        setIsSaving(true);
        setTimeout(() => {
          setIsSaving(false);
          alert('Job posting published successfully! Students can now view and apply.');
        }, 1500);
      }
    } else {
      alert('Please complete all required fields before publishing.');
    }
  };

  const handleSchedulePublish = () => {
    const scheduleDate = prompt('Enter publish date and time (YYYY-MM-DD HH:MM):');
    if (scheduleDate) {
      alert(`Job posting scheduled for publication on ${scheduleDate}`);
    }
  };

  const handleLoadTemplate = (template) => {
    setJobData((prev) => ({ ...prev, ...template }));
    setShowTemplateLibrary(false);
    alert('Template loaded successfully!');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <JobDetailsForm
            formData={jobData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      case 2:
        return (
          <EligibilityForm
            formData={jobData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      case 3:
        return (
          <InternshipDetailsForm
            formData={jobData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      case 4:
        return (
          <ApplicationManagementForm
            formData={jobData}
            errors={errors}
            onChange={handleDataChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-card-foreground mb-2">
              {jobData?.title || 'Create Job Posting'}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Create and publish job listings or internship opportunities
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setShowTemplateLibrary(true)}
              iconName="FileText"
              iconPosition="left"
            >
              Templates
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              iconName="Eye"
              iconPosition="left"
            >
              Preview
            </Button>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 md:p-6">
          <div className="mb-6 md:mb-8">
            <div className="flex items-center justify-between mb-4">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.id}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-smooth ${
                        currentStep === step?.id
                          ? 'bg-primary text-primary-foreground'
                          : currentStep > step?.id
                          ? 'bg-success text-success-foreground'
                          : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      <Icon
                        name={currentStep > step?.id ? 'Check' : step?.icon}
                        size={20}
                        color="currentColor"
                      />
                    </div>
                    <span
                      className={`text-xs md:text-sm mt-2 font-medium hidden md:block ${
                        currentStep === step?.id
                          ? 'text-primary'
                          : currentStep > step?.id
                          ? 'text-success' :'text-muted-foreground'
                      }`}
                    >
                      {step?.label}
                    </span>
                  </div>
                  {index < steps?.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-2 rounded transition-smooth ${
                        currentStep > step?.id ? 'bg-success' : 'bg-muted'
                      }`}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="md:hidden text-center">
              <span className="text-sm font-medium text-primary">
                {steps?.find((s) => s?.id === currentStep)?.label}
              </span>
            </div>
          </div>

          <div className="mb-6">{renderStepContent()}</div>

          <div className="flex flex-col md:flex-row gap-3 pt-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
              iconName="ChevronLeft"
              iconPosition="left"
            >
              Previous
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              loading={isSaving}
              iconName="Save"
              iconPosition="left"
            >
              Save Draft
            </Button>
            {currentStep < 4 ? (
              <Button
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Next
              </Button>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={handleSchedulePublish}
                  iconName="Calendar"
                  iconPosition="left"
                >
                  Schedule
                </Button>
                <Button
                  onClick={handlePublish}
                  loading={isSaving}
                  iconName="Send"
                  iconPosition="left"
                >
                  Publish Now
                </Button>
              </>
            )}
          </div>
        </div>

        {showPreview && (
          <PreviewPanel
            jobData={jobData}
            onClose={() => setShowPreview(false)}
          />
        )}

        {showTemplateLibrary && (
          <JobTemplateLibrary
            onLoadTemplate={handleLoadTemplate}
            onClose={() => setShowTemplateLibrary(false)}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default JobInternshipPosting;